import { Link, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { useHatcheryScope } from '../../lib/scope.js'
import {
  Badge, Card, Empty, ErrorBox, KV, Page, Spinner, StatusBadge, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { BATCH_STATUS, FLAG, fmtDate, inr, lakh } from '../../lib/format.js'

export default function BatchDetail () {
  const { id } = useParams()
  const scope = useHatcheryScope()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading, reload } = useAsync(() => api.get(`/hatchery/batches/${id}`), [id], { live: ['batches', 'orders'] })

  const setStatus = async status => {
    const ok = await confirm({
      title: status === 'active' ? 'Publish this batch?' : `Move this batch to ${status}?`,
      body: {
        active: 'The operator desk can book against it straight away and farmers see it on the public directory.',
        closed: 'It stops being offered for new bookings. Bookings already placed are not touched.',
        sold_out: 'It stops being offered for new bookings.',
        draft: 'It comes off the desk and off the public directory until you publish it again.'
      }[status] || 'This changes what the desk and farmers can see.',
      facts: [['Batch', data.batch.batchCode], ['Currently', data.batch.status]],
      confirmLabel: status === 'active' ? 'Publish batch' : `Move to ${status}`,
      tone: status === 'active' ? '' : 'danger'
    })
    if (!ok) return
    try {
      await api.post(`/hatchery/batches/${id}/status`, { status, ...scope.body })
      toast.ok(status === 'active' ? 'Batch published — the operator desk can book against it now.' : `Batch moved to ${status}.`)
      reload()
    } catch (err) { toast.error(err.message) }
  }

  const removeMedia = async mediaId => {
    const ok = await confirm({
      title: 'Remove this photo?',
      body: 'It disappears from the operator desk immediately. The file cannot be recovered from here.',
      confirmLabel: 'Remove photo',
      tone: 'danger'
    })
    if (!ok) return
    try {
      await api.del(`/hatchery/batches/${id}/media/${mediaId}`)
      toast.ok('Media removed.')
      reload()
    } catch (err) { toast.error(err.message) }
  }

  if (loading) return <Page title="Batch"><Spinner /></Page>
  if (error) return <Page title="Batch"><ErrorBox error={error} onRetry={reload} /></Page>

  const { batch: b, orders } = data
  const pcrClean = ['wssv', 'ehp', 'ihhnv'].every(k => b.pcr?.[k] === 'negative')

  return (
    <Page
      crumb={<Link to={`/hatchery${scope.link}`}>← My batches</Link>}
      title={b.batchCode}
      subtitle={`${b.species} · ${b.plStage} on ${fmtDate(b.readyDate)} · ${b.broodstockSource}`}
      actions={<>
        <Link className="btn" to={`/hatchery/batches/${id}/edit${scope.link}`}>Edit</Link>
        {b.status === 'draft' && <button className="btn primary" onClick={() => setStatus('active')}>Publish batch</button>}
        {b.status === 'active' && <button className="btn" onClick={() => setStatus('closed')}>Close batch</button>}
        {b.status === 'closed' && <button className="btn primary" onClick={() => setStatus('active')}>Reopen</button>}
      </>}
    >
      {b.status === 'draft' && (
        <div className="alert info">
          <div>
            <b>This batch is still a draft.</b>
            The operator desk cannot see it until you publish. Publishing needs a PCR certificate on file.
          </div>
        </div>
      )}
      {b.qualityFlag !== 'clear' && (
        <div className={`alert ${b.qualityFlag === 'red_flag' ? 'error' : 'warn'}`}>
          <div><b>{FLAG[b.qualityFlag].label}</b>{b.flagNote}</div>
        </div>
      )}

      <div className="grid four" style={{ marginBottom: 18 }}>
        <div className="stat"><div className="n">{lakh(b.availableLakh)}</div><div className="k">Available</div><div className="sub">of {lakh(b.totalCapacityLakh)} · {lakh(b.bookedLakh)} blocked</div></div>
        <div className="stat"><div className="n">{inr(b.basePricePer1000)}</div><div className="k">Per 1,000 PL</div><div className="sub">min order {lakh(b.minOrderLakh)}</div></div>
        <div className="stat"><div className="n">{b.stressTestPct ?? '—'}%</div><div className="k">Stress test survival</div><div className="sub">CV {b.sizeCvPct ?? '—'}%</div></div>
        <div className="stat"><div className="n">{b.samplePassCount}/{b.samplePassCount + b.sampleFailCount}</div><div className="k">Sample tests passed</div><div className="sub">24-hour pond survival</div></div>
      </div>

      <div className="split">
        <div>
          <Card title="Bookings against this batch" padded={false}>
            <div className="scroll-x">
              <table>
                <thead>
                  <tr><th>Order</th><th>Farmer</th><th>Stocking</th><th className="num">Qty</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o._id}>
                      <td className="mono">{o.orderCode}</td>
                      <td>{o.farmer?.name}<span className="t-sub">{o.farmer?.village}</span></td>
                      <td className="tight">{fmtDate(o.targetStockingDate)}</td>
                      <td className="num">{lakh(o.quantityLakh)}</td>
                      <td><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                  {!orders.length && <tr><td colSpan={5}><Empty>No bookings on this batch yet.</Empty></td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title={`Tank photos & clips (${b.media?.length || 0})`}>
            {b.media?.length
              ? (
                <div className="media-strip">
                  {b.media.map(m => (
                    <figure key={m._id}>
                      {m.kind === 'video'
                        ? <video src={m.path} controls />
                        : <a href={m.path} target="_blank" rel="noreferrer"><img src={m.path} alt={m.caption || ''} /></a>}
                      <figcaption>
                        {m.caption || m.kind}
                        <br />
                        <button className="btn ghost sm" onClick={() => removeMedia(m._id)}>Remove</button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
                )
              : <Empty>No media uploaded. Add photos or a short clip from the edit screen.</Empty>}
          </Card>
        </div>

        <div>
          <Card title="Batch record">
            <KV items={[
              { k: 'Status', v: <Badge tone={BATCH_STATUS[b.status].tone}>{BATCH_STATUS[b.status].label}</Badge> },
              { k: 'Quality flag', v: <Badge tone={FLAG[b.qualityFlag].tone}>{FLAG[b.qualityFlag].label}</Badge> },
              { k: 'Ready date', v: fmtDate(b.readyDate) },
              { k: 'PL stage', v: b.plStage },
              { k: 'Acclimated salinity', v: b.salinityPpt != null ? `${b.salinityPpt} ppt` : '—' },
              { k: 'Feed protocol', v: b.feedProtocol },
              { k: 'CAA licence', v: <span className="mono small">{b.caaLicenseNo || '—'}</span> }
            ]} />
          </Card>

          <Card title="PCR screening">
            <KV items={[
              { k: 'Laboratory', v: b.pcr?.labName },
              { k: 'Screened on', v: fmtDate(b.pcr?.screenDate) },
              { k: 'WSSV', v: <Badge tone={b.pcr?.wssv === 'negative' ? 'good' : 'bad'}>{b.pcr?.wssv}</Badge> },
              { k: 'EHP', v: <Badge tone={b.pcr?.ehp === 'negative' ? 'good' : 'bad'}>{b.pcr?.ehp}</Badge> },
              { k: 'IHHNV', v: <Badge tone={b.pcr?.ihhnv === 'negative' ? 'good' : 'bad'}>{b.pcr?.ihhnv}</Badge> }
            ]} />
            <div className="divider" />
            {b.pcr?.reportPath
              ? <a className="btn" href={b.pcr.reportPath} target="_blank" rel="noreferrer">Open certificate</a>
              : <div className="alert warn" style={{ marginBottom: 0 }}><div>No certificate on file — required before publishing.</div></div>}
            {b.pcr?.reportPath && !pcrClean && (
              <div className="hint">One or more pathogens are not marked negative — the desk will see this.</div>
            )}
          </Card>

          {b.offer?.type && b.offer.type !== 'none' && (
            <Card title="Active offer">
              <div className="pill-offer">{b.offer.details || b.offer.type}</div>
              <div className="divider" />
              <KV items={[
                { k: 'Type', v: b.offer.type.replace(/_/g, ' ') },
                { k: 'Value', v: b.offer.value },
                { k: 'Applies above', v: b.offer.minQtyLakh ? lakh(b.offer.minQtyLakh) : '—' },
                { k: 'Valid', v: `${fmtDate(b.offer.start)} → ${fmtDate(b.offer.end)}` }
              ]} />
            </Card>
          )}
        </div>
      </div>
    </Page>
  )
}
